import React, { useState } from 'react';
import { Upload, Select, message, Progress, Space, Typography } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadProps, RcFile } from 'antd/es/upload';
import { uploadAttachment } from '../../../../services/attachment';

const { Dragger } = Upload;
const { Option } = Select;
const { Text } = Typography;

interface FileUploaderProps {
  caseId: number;
  onUploadSuccess: () => void;
  onUploadError?: (error: any) => void;
}

// File categories for case attachments
const FILE_CATEGORIES = [
  { value: 'contract', label: '合同文件' },
  { value: 'evidence', label: '证据材料' },
  { value: 'legal_doc', label: '法律文书' },
  { value: 'other', label: '其他文件' },
];

// Allowed file types
const ALLOWED_EXTENSIONS = [
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
  'jpg', 'jpeg', 'png', 'gif', 'bmp',
  'txt', 'zip', 'rar', '7z',
];

// Max file size: 50MB
const MAX_FILE_SIZE = 50 * 1024 * 1024;

const FileUploader: React.FC<FileUploaderProps> = ({
  caseId,
  onUploadSuccess,
  onUploadError,
}) => {
  const [category, setCategory] = useState<string>('other');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Validate file before upload
  const beforeUpload = (file: RcFile): boolean => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      message.error(`文件大小不能超过 50MB`);
      return false;
    }

    // Check file type
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !ALLOWED_EXTENSIONS.includes(fileExtension)) {
      message.error(
        `不支持的文件类型。支持的类型：${ALLOWED_EXTENSIONS.join(', ')}`
      );
      return false;
    }

    return true;
  };

  // Custom upload request
  const customRequest: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError, onProgress } = options;

    if (!category) {
      message.error('请选择文件分类');
      onError?.(new Error('Category not selected'));
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      // Upload using service function
      const response = await uploadAttachment(
        file as File,
        'case',
        caseId,
        category
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      message.success('上传成功');
      onSuccess?.(response);
      onUploadSuccess();
      
      setTimeout(() => {
        setUploadProgress(0);
        setUploading(false);
      }, 500);
    } catch (error: any) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.detail || error.message || '上传失败';
      message.error(errorMessage);
      onError?.(error);
      onUploadError?.(error);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="file-uploader">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Category Selector */}
        <div>
          <Text strong style={{ marginRight: 8 }}>
            文件分类：
          </Text>
          <Select
            value={category}
            onChange={setCategory}
            style={{ width: 200 }}
            disabled={uploading}
          >
            {FILE_CATEGORIES.map((cat) => (
              <Option key={cat.value} value={cat.value}>
                {cat.label}
              </Option>
            ))}
          </Select>
        </div>

        {/* Upload Dragger */}
        <Dragger
          name="file"
          multiple={false}
          customRequest={customRequest}
          beforeUpload={beforeUpload}
          showUploadList={false}
          disabled={uploading}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
          <p className="ant-upload-hint">
            支持 PDF、Word、Excel、PPT、图片等格式，单个文件不超过 50MB
          </p>
        </Dragger>

        {/* Upload Progress */}
        {uploading && uploadProgress > 0 && (
          <div>
            <Text type="secondary">上传中...</Text>
            <Progress percent={uploadProgress} status="active" />
          </div>
        )}
      </Space>
    </div>
  );
};

export default FileUploader;
